import type { DrawerProps } from "@octopusdeploy/design-system-components";
import { Drawer } from "@octopusdeploy/design-system-components";
import type { ReactNode } from "react";
import { useContext, createContext, useMemo } from "react";
import { createHtmlPortalNode, InPortal, OutPortal } from "react-reverse-portal";
export function useDrawerNode() {
    const drawerNode = useMemo(() => createHtmlPortalNode<typeof Drawer>(), []);
    const DrawerInPortal = useMemo(() => (props: DrawerProps) => (<InPortal node={drawerNode}>
                <Drawer {...props}/>
            </InPortal>), [drawerNode]);
    const DrawerOutPortal = useMemo(() => (props: Partial<DrawerProps>) => (
    // Wrapper div needed to work around react-reverse-portal issue
    // Possibly related to https://github.com/httptoolkit/react-reverse-portal/issues/22
    <div>
                <OutPortal<typeof Drawer> node={drawerNode} {...props}/>
            </div>), [drawerNode]);
    return { DrawerInPortal, DrawerOutPortal };
}
type DrawerOutPortalComponent = (props: Partial<DrawerProps>) => ReactNode;
const drawerOutPortalContext = createContext<DrawerOutPortalComponent | null>(null);
export function DrawerOutPortalProvider({ drawerOutPortal, children }: {
    drawerOutPortal: DrawerOutPortalComponent;
    children: ReactNode;
}) {
    return <drawerOutPortalContext.Provider value={drawerOutPortal}>{children}</drawerOutPortalContext.Provider>;
}
export function useDrawerOutPortalProvider() {
    const drawerOutPortal = useContext(drawerOutPortalContext);
    if (drawerOutPortal === null) {
        throw new Error("Drawer Out Portal Provider not found");
    }
    return drawerOutPortal;
}
